import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, GeoJSON } from "react-leaflet";
import L from "leaflet";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Compass, MapPin, Train, Shield, Sparkles, Globe2, Clock, Sun, Coins, BedDouble, ExternalLink } from "lucide-react";
import { useRouteBuilder } from "../context/RouteContext";
import { hostelworldCityUrl, gygLandmarkUrl, trackAffiliateClick } from "../lib/affiliates";

/* Esri World Imagery — real aerial photography of Europe. Free, no key,
   licence allows commercial use with attribution. */
const TILES = {
  url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  attribution: 'Imagery © <a href="https://www.esri.com/">Esri</a> · Earthstar Geographics',
};

/* Europe countries GeoJSON — fetched once for the country-on-hover glow. */
const EUROPE_GEOJSON_URL = "https://cdn.jsdelivr.net/gh/leakyMirror/map-of-europe@master/GeoJSON/europe.geojson";

/* Hardcoded per-country traveller meta. Tiny lookup, fast, no extra requests.
   Currency / language / time-zone offset / best-season hint per ISO-2 code. */
const COUNTRY_META = {
  FR: { currency: "€ EUR",   languages: "French",            tz: "CET",  bestMonths: "May – Sep" },
  NL: { currency: "€ EUR",   languages: "Dutch · English",   tz: "CET",  bestMonths: "Apr – Sep" },
  DE: { currency: "€ EUR",   languages: "German",            tz: "CET",  bestMonths: "May – Oct" },
  IT: { currency: "€ EUR",   languages: "Italian",           tz: "CET",  bestMonths: "Apr – Jun · Sep – Oct" },
  ES: { currency: "€ EUR",   languages: "Spanish · Catalan", tz: "CET",  bestMonths: "Mar – Jun · Sep – Oct" },
  CZ: { currency: "Kč CZK",  languages: "Czech",             tz: "CET",  bestMonths: "May – Sep" },
  AT: { currency: "€ EUR",   languages: "German",            tz: "CET",  bestMonths: "May – Sep · Dec markets" },
  PT: { currency: "€ EUR",   languages: "Portuguese",        tz: "WET",  bestMonths: "Apr – Oct" },
  HU: { currency: "Ft HUF",  languages: "Hungarian",         tz: "CET",  bestMonths: "May – Sep" },
  CH: { currency: "CHF",     languages: "German · French · Italian", tz: "CET", bestMonths: "Jun – Sep · Dec – Feb" },
  DK: { currency: "kr DKK",  languages: "Danish · English",  tz: "CET",  bestMonths: "May – Sep" },
  BE: { currency: "€ EUR",   languages: "Dutch · French",    tz: "CET",  bestMonths: "Apr – Sep" },
  PL: { currency: "zł PLN",  languages: "Polish",            tz: "CET",  bestMonths: "May – Sep" },
  SE: { currency: "kr SEK",  languages: "Swedish · English", tz: "CET",  bestMonths: "Jun – Aug · Dec lights" },
  GB: { currency: "£ GBP",   languages: "English",           tz: "GMT",  bestMonths: "May – Sep" },
};

/* Rough nightly hostel-tier estimate per city (€). Mirrors backend medians so
   the banner can show a useful "budget bed" hint without a network call. */
const NIGHTLY_EUR = {
  paris: 48, amsterdam: 45, berlin: 32, rome: 38, barcelona: 35, prague: 22,
  vienna: 30, lisbon: 28, budapest: 20, zurich: 65, copenhagen: 55, munich: 40,
  madrid: 32, florence: 38, venice: 50, brussels: 38, krakow: 18, stockholm: 50,
  edinburgh: 35, porto: 25, hamburg: 35,
};

const featureISO2 = (f) => {
  const p = f?.properties || {};
  return (p.ISO2 || p.iso_a2 || p.ISO_A2 || p.iso2 || "").toUpperCase();
};

/* Label-as-marker: the floating city name IS the clickable, hoverable marker.
   Hovering the name triggers the country glow; the marker has no dot/pin. */
const cityIcon = (name, visited) =>
  L.divIcon({
    className: "",
    iconSize: undefined,           // let the label size itself naturally
    iconAnchor: [0, 12],           // anchor near the bottom-center of the label
    html: `<span class="gem-label ${visited ? "gem-label--visited" : ""}">${name}</span>`,
  });

function AnimatedTrain({ points }) {
  const [t, setT] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    if (points.length < 2) return;
    let start;
    const duration = Math.max(8000, points.length * 4000);
    const step = (ts) => {
      if (!start) start = ts;
      setT(((ts - start) % duration) / duration);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [points]);

  if (points.length < 2) return null;

  const segs = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const [a, b] = [points[i], points[i + 1]];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    segs.push({ a, b, len, cum: total });
    total += len;
  }
  if (total === 0) return null;

  const target = t * total;
  const seg = segs.find((s) => target >= s.cum && target <= s.cum + s.len) || segs[segs.length - 1];
  const f = (target - seg.cum) / (seg.len || 1);
  const lat = seg.a[0] + (seg.b[0] - seg.a[0]) * f;
  const lon = seg.a[1] + (seg.b[1] - seg.a[1]) * f;
  const angle = Math.atan2(seg.b[0] - seg.a[0], seg.b[1] - seg.a[1]) * (180 / Math.PI);

  const icon = L.divIcon({
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: `<div class="train-marker" style="transform: translate(-50%,-50%) rotate(${-angle}deg)">
             <svg viewBox="0 0 24 24" width="22" height="22" fill="#f5d27a" stroke="#0a111c" stroke-width="1">
               <rect x="4" y="9" width="14" height="7" rx="1.5"/>
               <rect x="18" y="11" width="3" height="4" rx="0.6"/>
               <circle cx="8" cy="17.5" r="2" fill="#0a111c" stroke="#f5d27a"/>
               <circle cx="15" cy="17.5" r="2" fill="#0a111c" stroke="#f5d27a"/>
               <rect x="6" y="11" width="3" height="2.5" fill="#0a111c"/>
               <rect x="11" y="11" width="3" height="2.5" fill="#0a111c"/>
             </svg>
           </div>`,
  });
  return <Marker position={[lat, lon]} icon={icon} interactive={false} />;
}

export default function MapPage() {
  const [cities, setCities] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [countriesGeo, setCountriesGeo] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { segments } = useRouteBuilder();

  useEffect(() => {
    api.get("/cities").then((r) => {
      setCities(r.data);
      const slug = params.get("city");
      if (slug) navigate(`/city/${slug}`);
    });
    fetch(EUROPE_GEOJSON_URL).then((r) => r.json()).then(setCountriesGeo).catch(() => {});
  }, []);

  const routeLine = useMemo(() => {
    if (!cities.length || !segments.length) return [];
    const pts = [];
    segments.forEach((seg) => {
      const f = cities.find((c) => seg.from_city && seg.from_city.toLowerCase().includes(c.name.toLowerCase()));
      const t = cities.find((c) => seg.to_city && seg.to_city.toLowerCase().includes(c.name.toLowerCase()));
      if (f && (!pts.length || pts[pts.length - 1][0] !== f.lat)) pts.push([f.lat, f.lon]);
      if (t) pts.push([t.lat, t.lon]);
    });
    return pts;
  }, [cities, segments]);

  const hoveredCountryFeature = useMemo(() => {
    if (!hovered || !countriesGeo) return null;
    const target = (hovered.country_code || "").toUpperCase();
    return countriesGeo.features.find((f) => featureISO2(f) === target) || null;
  }, [hovered, countriesGeo]);

  const meta = hovered ? COUNTRY_META[(hovered.country_code || "").toUpperCase()] : null;
  const nightly = hovered ? NIGHTLY_EUR[hovered.slug] : null;

  return (
    <div data-testid="map-page" className="pt-safe-navbar relative">
      <div className="h-[calc(100vh-4rem)] w-full relative atlas-moody">
        <MapContainer
          center={[50.5, 10]}
          zoom={5}
          minZoom={3}
          scrollWheelZoom
          className="h-full w-full"
          style={{ background: "#0d1525" }}
        >
          <TileLayer url={TILES.url} attribution={TILES.attribution} />

          {/* Hovered country highlight — deep-navy glow that matches the brand */}
          {hoveredCountryFeature && (
            <GeoJSON
              key={hovered.country_code}
              data={hoveredCountryFeature}
              style={() => ({
                color: "#3a5a8c",       // navy stroke
                weight: 2,
                opacity: 1,
                fillColor: "#0d1525",   // deep navy fill
                fillOpacity: 0.4,
                className: "country-hover-layer",
              })}
              interactive={false}
            />
          )}

          {/* User-built route polyline + animated train (no static corridors) */}
          {routeLine.length >= 2 && (
            <>
              <Polyline
                positions={routeLine}
                pathOptions={{
                  color: "#f5f0e1",
                  weight: 3.5,
                  opacity: 0.9,
                  dashArray: "8 10",
                  lineCap: "round",
                  className: "route-polyline",
                }}
              />
              <AnimatedTrain points={routeLine} />
            </>
          )}

          {/* City labels — the floating name itself is the hover target.
              Hovering the name highlights its country; clicking opens it. */}
          {cities.map((c) => {
            const visited = segments.some((s) =>
              [s.from_city, s.to_city].some((n) => n && n.toLowerCase().includes(c.name.toLowerCase()))
            );
            return (
              <Marker
                key={c.slug}
                position={[c.lat, c.lon]}
                icon={cityIcon(c.name, visited)}
                eventHandlers={{
                  click: () => navigate(`/city/${c.slug}`),
                  mouseover: () => setHovered(c),
                  mouseout: () => setHovered(null),
                }}
              />
            );
          })}
        </MapContainer>

        {/* Top-left legend */}
        <div className="absolute top-4 left-4 z-[500] iq-card px-4 py-3 max-w-xs pointer-events-auto">
          <div className="iq-overline mb-1"><Compass className="inline h-3 w-3 mr-1" /> Europe Atlas · Satellite</div>
          <p className="text-cream-dim text-xs leading-relaxed">
            Hover a city to glow its country gold. Tap a label to open its landmarks and safety-rated stays.
          </p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-cream-dim/80 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block px-1.5 py-0.5 text-[8px] tracking-widest font-display bg-ink/80 border border-cream/20 text-cream rounded-sm">CITY</span>
              Curated
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block px-1.5 py-0.5 text-[8px] tracking-widest font-display bg-gold/90 border border-gold text-ink rounded-sm">CITY</span>
              On your route
            </span>
          </div>
        </div>

        {/* Hovered city — vertical side banner with fade after Don't Miss */}
        {hovered && (
          <div
            data-testid="map-hover-banner"
            className="absolute top-4 bottom-4 right-4 z-[500] w-[300px] md:w-[340px] iq-card overflow-hidden flex flex-col animate-fade-in shadow-2xl shadow-black/70"
            // Banner is now interactive (affiliate links). Keep it open while
            // the user moves their cursor inside it.
            onMouseEnter={() => setHovered(hovered)}
          >
            <div className="relative h-56 w-full overflow-hidden shrink-0">
              <img src={hovered.image} alt={hovered.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/30 to-transparent" />
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                <span className="iq-overline text-[10px] bg-ink/80 px-2 py-1 rounded-sm border border-panel-border">
                  {hovered.country}
                </span>
                <span className="text-[10px] font-display tracking-widest bg-gold/15 text-gold border border-gold/40 px-2 py-1 rounded-sm">
                  +{hovered.xp} XP
                </span>
              </div>
              <div className="absolute bottom-3 left-4 right-4">
                <div className="font-display text-cream text-3xl tracking-wider leading-none">{hovered.name}</div>
                <div className="text-cream-dim/80 text-[11px] mt-1 inline-flex items-center gap-1">
                  <Train className="h-3 w-3 text-gold" /> {hovered.station_name}
                </div>
              </div>
            </div>

            {/* Scrollable content with fade-out mask below "Don't miss" */}
            <div
              className="flex-1 relative overflow-hidden"
              style={{
                WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 78%, transparent 100%)",
                maskImage: "linear-gradient(to bottom, #000 0%, #000 78%, transparent 100%)",
              }}
            >
              <div className="p-4 flex flex-col gap-4">
                <p className="text-cream-dim text-sm font-serif italic leading-relaxed">
                  {hovered.tagline}
                </p>

                {/* Good to know — currency, language, time, best season, bed */}
                {(meta || nightly) && (
                  <div>
                    <div className="iq-overline text-[10px] mb-2">
                      <Sparkles className="inline h-3 w-3 mr-1" /> Good to know
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      {meta?.currency && (
                        <InfoChip icon={<Coins className="h-3 w-3" />} label="Currency" value={meta.currency} />
                      )}
                      {meta?.languages && (
                        <InfoChip icon={<Globe2 className="h-3 w-3" />} label="Language" value={meta.languages} />
                      )}
                      {meta?.tz && (
                        <InfoChip icon={<Clock className="h-3 w-3" />} label="Time zone" value={meta.tz} />
                      )}
                      {meta?.bestMonths && (
                        <InfoChip icon={<Sun className="h-3 w-3" />} label="Best season" value={meta.bestMonths} />
                      )}
                      {nightly != null && (
                        <a
                          href={hostelworldCityUrl(hovered.name)}
                          target="_blank"
                          rel="sponsored noopener noreferrer"
                          onClick={() => trackAffiliateClick("hostelworld", { city: hovered.name, source: "map_banner" })}
                          className="col-span-2 bg-ink/60 border border-panel-border hover:border-gold/60 hover:bg-ink/80 rounded-sm px-2.5 py-2 transition-colors group"
                        >
                          <div className="text-[9px] uppercase tracking-widest text-gold/80 flex items-center gap-1">
                            <BedDouble className="h-3 w-3" /> Hostel bed / night
                            <ExternalLink className="h-2.5 w-2.5 ml-auto opacity-60 group-hover:opacity-100" />
                          </div>
                          <div className="text-cream text-xs mt-0.5 leading-tight">
                            ~€{nightly} <span className="text-cream-dim/70">· Find a bed on Hostelworld →</span>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {hovered.landmarks?.length > 0 && (
                  <div>
                    <div className="iq-overline text-[10px] mb-1.5">
                      <MapPin className="inline h-3 w-3 mr-1" /> Don't miss
                    </div>
                    <ul className="text-cream-dim text-xs space-y-1">
                      {hovered.landmarks.slice(0, 4).map((l) => (
                        <li key={l}>
                          <a
                            href={gygLandmarkUrl(l, hovered.name)}
                            target="_blank"
                            rel="sponsored noopener noreferrer"
                            onClick={() => trackAffiliateClick("gyg", { landmark: l, city: hovered.name, source: "map_banner_landmark" })}
                            className="flex items-start gap-1.5 hover:text-cream transition-colors group"
                          >
                            <Sparkles className="h-3 w-3 text-gold mt-0.5 shrink-0" />
                            <span className="flex-1">{l}</span>
                            <ExternalLink className="h-2.5 w-2.5 mt-0.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                          </a>
                        </li>
                      ))}
                    </ul>
                    <div className="text-[9px] tracking-widest uppercase text-gold/60 mt-1.5">
                      Skip-the-line via GetYourGuide
                    </div>
                  </div>
                )}

                <div className="pt-1 pb-6 text-[10px] tracking-widest uppercase text-gold/80 font-display flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Click the label for safety-rated stays
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoChip({ icon, label, value, className = "" }) {
  return (
    <div className={`bg-ink/60 border border-panel-border rounded-sm px-2.5 py-2 ${className}`}>
      <div className="text-[9px] uppercase tracking-widest text-gold/80 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-cream text-xs mt-0.5 leading-tight">{value}</div>
    </div>
  );
}
